import PouchDB from "pouchdb-browser";
import api from "@shared/server/api";
import { getLocalToken } from "@shared/functions";
import { pouches_db, localStorageCommonMirror, localStorageAuthv2 } from "../config";

/**
 * Recover the lost cuadre context (`common`) after an IndexedDB eviction, where
 * the Redux store was wiped but localStorage survived (JWT + cobro_auth.user).
 *
 * Layered, most-durable first:
 *   1. localStorage mirror of `common` — instant, full state (bank/perms), offline.
 *      Validated against the authenticated route so we never restore another
 *      user's cuadre.
 *   2. `cuadre/init` — rebuild from the server using the surviving identity in
 *      cobro_auth.user (creditor_id + user_id, both present for every logged-in
 *      user). Needs network; same call the login flow makes, so it's safe to repeat.
 *
 * @returns {Promise<{source: "localstorage"|"cuadre_init", common: object} | null>}
 *          the recovered common (caller dispatches setCommon), or null if it can't.
 */
function readAuthUser() {
  try {
    return JSON.parse(localStorage.getItem(localStorageAuthv2) || "null")?.user ?? null;
  } catch {
    return null;
  }
}

// Rebuild the offline-data "common" doc from the recovered common. The login flow
// writes this doc, and runtime paths (e.g. the sync-queue createNewQueue) read it
// DIRECTLY from PouchDB — recovering only the Redux copy left it missing after an
// eviction, breaking gasto/payment queueing. Local write, no network. Never throws.
async function restoreOfflineCommon(common) {
  try {
    const db = new PouchDB(pouches_db.OFFLINE_DATA);
    let existing = null;
    try {
      existing = await db.get("common");
    } catch {
      // not present -> create fresh
    }
    const meta = existing?.meta || { expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000 };
    await db.put({ ...(existing || {}), ...common, meta, _id: "common", _rev: existing?._rev });
  } catch {
    // best-effort; the queue path also tolerates a missing doc now
  }
}

export async function recoverCommon() {
  const user = readAuthUser();

  // Layer 1 — localStorage mirror.
  try {
    const raw = localStorage.getItem(localStorageCommonMirror);
    if (raw) {
      const mirror = JSON.parse(raw);
      const belongsToUser = !user || !user.creditor_id || String(mirror?.creditor_id) === String(user.creditor_id);
      if (mirror && mirror.creditor_id && belongsToUser) {
        await restoreOfflineCommon(mirror);
        return { source: "localstorage", common: mirror };
      }
    }
  } catch {
    // ignore, fall through
  }

  // Layer 2 — rebuild from the server via cuadre/init using the surviving identity.
  if (user && user.creditor_id && user.user_id) {
    try {
      const token = getLocalToken();
      const { data: dbCommon } = await api.post("/cobro/cuadre/init", {
        creditor_id: user.creditor_id,
        owner_id: user.owner_id,
        user_id: user.user_id,
        token,
        env: import.meta.env.VITE_ENV,
        appVersion: import.meta.env.VITE_VERSION,
      });
      if (dbCommon && dbCommon.creditor_id) {
        await restoreOfflineCommon(dbCommon);
        return { source: "cuadre_init", common: dbCommon };
      }
    } catch {
      // network / server error -> could not recover
    }
  }

  return null;
}
